import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, X } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useShop } from "@/context/ShopContext";
import { img, money, FREE_SHIPPING } from "@/lib/api";

export const QtyControl = ({ value, onChange, testId }) => (
  <div className="inline-flex items-center border border-[var(--mm-line)]">
    <button data-testid={`${testId}-decrease`} onClick={() => onChange(value - 1)} className="px-2.5 py-1.5 hover:text-[var(--mm-red)]" aria-label="Decrease"><Minus size={12} /></button>
    <span data-testid={`${testId}-value`} className="w-7 text-center text-xs">{value}</span>
    <button data-testid={`${testId}-increase`} onClick={() => onChange(value + 1)} className="px-2.5 py-1.5 hover:text-[var(--mm-red)]" aria-label="Increase"><Plus size={12} /></button>
  </div>
);

export const BagDrawer = () => {
  const { items, bagOpen, setBagOpen, setQty, remove, subtotal, keyOf, count } = useShop();
  const navigate = useNavigate();
  const left = Math.max(0, FREE_SHIPPING - subtotal);

  return (
    <Sheet open={bagOpen} onOpenChange={setBagOpen}>
      <SheetContent data-testid="bag-drawer" side="right" className="w-full sm:max-w-md bg-[var(--mm-cream)] p-0 flex flex-col border-l border-[var(--mm-line)] [&>button]:hidden">
        <div className="flex items-center justify-between px-6 py-6 border-b border-[var(--mm-line)]">
          <SheetTitle className="font-serif-mm text-3xl font-normal">Your bag <span className="text-[var(--mm-red)] italic">({count})</span></SheetTitle>
          <SheetDescription className="sr-only">Items in your shopping bag</SheetDescription>
          <button data-testid="bag-close-button" onClick={() => setBagOpen(false)} aria-label="Close"><X size={20} /></button>
        </div>
        {items.length > 0 && (
          <div className="px-6 py-4 border-b border-[var(--mm-line)]">
            <p data-testid="free-shipping-progress-text" className="text-xs mb-2">{left > 0 ? <>Add <b>{money(left)}</b> for complimentary UK delivery</> : "You've unlocked complimentary UK delivery"}</p>
            <div className="h-[2px] bg-[var(--mm-line)]"><div className="h-full bg-[var(--mm-red)] transition-[width] duration-500" style={{ width: `${Math.min(100, (subtotal / FREE_SHIPPING) * 100)}%` }} /></div>
          </div>
        )}
        <div className="flex-1 overflow-y-auto px-6">
          {items.length === 0 ? (
            <div data-testid="bag-empty-state" className="h-full flex flex-col items-start justify-center gap-6">
              <p className="font-serif-mm text-4xl leading-tight">Your bag is<br /><em className="text-[var(--mm-red)]">beautifully empty.</em></p>
              <button data-testid="bag-empty-shop-button" className="btn-mm" onClick={() => { setBagOpen(false); navigate("/shop"); }}>Shop the collection</button>
            </div>
          ) : items.map((it) => {
            const k = keyOf(it);
            return (
              <div key={k} data-testid="bag-item" className="flex gap-4 py-5 border-b border-[var(--mm-line)]">
                <Link to={`/product/${it.slug}`} onClick={() => setBagOpen(false)} className="w-20 shrink-0 aspect-[3/4] bg-[var(--mm-sand)] overflow-hidden"><img src={img(it.image)} alt={it.name} className="w-full h-full object-cover" /></Link>
                <div className="flex-1 min-w-0 flex flex-col">
                  <div className="flex justify-between gap-3">
                    <p className="text-sm font-semibold leading-snug">{it.name}</p>
                    <p data-testid="bag-item-line-total" className="text-sm whitespace-nowrap">{money(it.price * it.quantity)}</p>
                  </div>
                  <p className="text-xs text-[var(--mm-muted)] mt-1">Size {it.size}{it.colour ? ` · ${it.colour}` : ""}{it.preorder && <span className="text-[var(--mm-red)]"> · Pre-order</span>}</p>
                  <div className="mt-auto flex items-center justify-between pt-3">
                    <QtyControl testId="bag-item-qty" value={it.quantity} onChange={(q) => setQty(k, q)} />
                    <button data-testid="bag-item-remove-button" onClick={() => remove(k)} className="text-[0.65rem] tracking-[0.2em] uppercase underline underline-offset-4 hover:text-[var(--mm-red)]">Remove</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {items.length > 0 && (
          <div className="px-6 py-6 border-t border-[var(--mm-line)] space-y-4">
            <div className="flex justify-between text-sm"><span className="eyebrow">Subtotal</span><span data-testid="bag-subtotal" className="font-semibold">{money(subtotal)}</span></div>
            <p className="text-xs text-[var(--mm-muted)]">Delivery calculated at checkout. Secure online payment.</p>
            <button data-testid="bag-checkout-button" className="btn-mm w-full" onClick={() => { setBagOpen(false); navigate("/checkout"); }}>Checkout</button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
